import {
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EventDetailProps } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '../../services/redux/store';
import { Image } from '../../components/Image';
import { STYLE_VARS } from '../../constants/style-vars';
import { Button } from '../../components/Button';
import { Footer } from '../../components/Footer';
import EventSignUpForm from './components/EventSignUpForm';
import PageTitle from '../../components/PageTitle';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { EventsLayout } from '../../WebLayout';
import { COLORS, FONTS } from '../../constants';
import { IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import { event_add_button } from '../../assets/base64';
import { IEvent } from '../../types';
import { EventsApi } from '../../services/api';
import { Loading } from '../../components';
import RenderHtml from 'react-native-render-html';
import { IFormData } from '../../components/FormHelper/FormHelper.types';
import { Alert } from '../../services/alert';
import dayjs from 'dayjs';
import { API_URI } from '../../constants/env';
import { convertUnixToUTCDate } from '../../services/utils/dateConvert';

const EventDetail: FC<EventDetailProps> = ({ navigation, route }) => {
  const id = route.params.id;
  const isDesktop = useIsDesktop();
  const scrollViewRef = useRef<ScrollView>(null);

  const [event, setEvent] = useState<IEvent>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { width } = useWindowDimensions();
  const [contentHeight, setContentHeight] = useState(0);
  const [posterHeight, setPosterHeight] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);

  const getEvents = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await EventsApi.getEvent(id);
      setEvent(res.data);
      console.log(res.data);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getEventCalendarFile = useCallback(async (id: number) => {
    try {
      const res = await EventsApi.getEventCalendarFile(id);
      const href = window.URL.createObjectURL(res.data);
      const anchorElement = document.createElement('a');
      anchorElement.href = href;
      anchorElement.download = 'event-calendar';
      anchorElement.click();
    } catch (error: any) {
      console.log(error);
    } finally {
    }
  }, []);

  const onSubmitHandler = useCallback(
    async (data: IFormData) => {
      setIsSubmitting(true);
      const submitData = {
        adults: data.nAdults,
        children: data.nChildren,
        special_request: data.special,
      };

      const successMsg = `You sign up request to ${event?.title} on ${dayjs(
        new Date()
      ).format('DD-MM-YYYY HH:mm:ss')} is successful`;
      EventsApi.postSignUpEvent(id, submitData)
        .then((res) => {
          Alert.alert('Success', res.data.message);
        })
        .catch((error) => {
          Alert.alert('Sign Up Alert', error.message);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [id, event]
  );

  useEffect(() => {
    getEvents(id);
  }, [id, getEvents]);

  const buttons = useMemo(() => {
    return [
      {
        title: 'Add to ical',
        onPress: () => {
          if (IS_WEB) {
            getEventCalendarFile(id);
          } else {
            Linking.openURL(
              `${API_URI}/wp-json/kcc/v1/events/` + id + '/calendar'
            );
          }
        },
        icon: IS_WEB
          ? addBase64Prefix(event_add_button)
          : require('../../assets/event-add-button.png'),
      },
      {
        title: 'Add to google calendar',
        onPress: () => {
          if (IS_WEB) {
            getEventCalendarFile(id);
          } else {
            Linking.openURL(
              `${API_URI}/wp-json/kcc/v1/events/` + id + '/calendar'
            );
          }
        },
        icon: IS_WEB
          ? addBase64Prefix(event_add_button)
          : require('../../assets/event-add-button.png'),
      },
    ];
  }, [id, getEventCalendarFile]);

  const kccHeader = useMemo(() => {
    return (
      <View style={styles.headerWrapper}>
        <View style={styles.lineHeader} />
        <View style={{ paddingHorizontal: 10 }}>
          <Text style={styles.headerText}>The KCC</Text>
        </View>
        <View style={styles.lineHeader} />
      </View>
    );
  }, []);

  const signUpScrollHeight = useMemo(() => {
    return (
      contentHeight + posterHeight + thumbHeight + (isDesktop() ? 400 : 300)
    );
  }, [contentHeight, posterHeight, thumbHeight, isDesktop]);

  if (isDesktop()) {
    return (
      <EventsLayout scrollViewRef={scrollViewRef}>
        {isLoading === false && event ? (
          <View>
            {!event.poster && event.thumb && (
              <Image
                source={{ uri: event?.thumb }}
                style={{
                  width: '100%',
                  height: 540,
                  resizeMode: 'cover',
                  marginBottom: 30,
                }}
              />
            )}
            <View style={[styles.headerWrapper, { marginBottom: 20 }]}>
              <View style={styles.lineHeader} />
              <View style={{ paddingHorizontal: 10, maxWidth: '80%' }}>
                <Text
                  style={{
                    fontFamily: FONTS.IowanOldStyleBTRoman,
                    fontSize: 22,
                    fontWeight: '500',
                    color: COLORS.GREEN,
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  }}
                >
                  {event?.title}
                </Text>
              </View>
              <View style={styles.lineHeader} />
            </View>
            <Text style={styles.dateText}>
              {convertUnixToUTCDate(event.start).format('DD-MM-YYYY')}
              <Text style={{ paddingHorizontal: 5 }}>-</Text>
              {convertUnixToUTCDate(event.end).format('DD-MM-YYYY')}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.AvenirMedium,
                fontSize: 15,
                fontWeight: '500',
                textAlign: 'justify',
                color: COLORS.DARKER_GREY,
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 20,
              }}
              onLayout={(e) => {
                var { height } = e.nativeEvent.layout;
                setContentHeight(height);
              }}
            >
              <RenderHtml
                contentWidth={width / 2.05}
                source={{ html: event.content }}
              />
            </Text>
            {event.signup_available && (
              <View style={{ flexDirection: 'row', marginBottom: 30 }}>
                <Button
                  text="SIGN UP"
                  style={STYLE_VARS.blueButton}
                  onPress={() => {
                    if (scrollViewRef.current) {
                      scrollViewRef.current.scrollTo({
                        y: signUpScrollHeight,
                        animated: true,
                      });
                    }
                  }}
                />
              </View>
            )}

            <View style={{ flexDirection: 'row' }}>
              {buttons.map((button) => (
                <TouchableOpacity
                  key={button.title}
                  style={{
                    marginBottom: 10,
                    alignItems: 'center',
                    flexDirection: 'row',
                    alignSelf: 'flex-start',
                    paddingVertical: 5,
                    marginEnd: 50,
                  }}
                  onPress={button.onPress}
                >
                  <Image
                    source={button.icon}
                    style={{
                      width: 18,
                      height: 18,
                      resizeMode: 'contain',
                      marginRight: 10,
                    }}
                  />
                  <Text
                    style={{
                      fontFamily: FONTS.IowanOldStyleBTRoman,
                      fontSize: 14,
                      fontWeight: '500',
                      color: STYLE_VARS.textColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    {button.title}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {event?.poster && (
              <Image
                source={{ uri: event?.poster || '' }}
                style={{
                  width: '100%',
                  height: 1300,
                  resizeMode: 'cover',
                  marginVertical: 20,
                }}
                onLayout={(e) => {
                  var { height } = e.nativeEvent.layout;
                  setPosterHeight(height);
                }}
              />
            )}
            {event.signup_available && (
              <EventSignUpForm onSubmitHandler={onSubmitHandler} />
            )}
          </View>
        ) : isLoading === true ? (
          <Loading />
        ) : (
          <></>
        )}
        {isSubmitting === true && <Loading isFullScreen={true} />}
      </EventsLayout>
    );
  }

  return (
    <ScrollView showsVerticalScrollIndicator={false} ref={scrollViewRef}>
      <View style={styles.container}>
        <PageTitle
          title="Events"
          icon={require('../../assets/events.png')}
          haveGoBackButton={true}
        />
      </View>
      {isLoading === false && event ? (
        <View style={{ paddingBottom: 30 }}>
          {!event.poster && event.thumb && (
            <View style={styles.container}>
              <Image
                source={{ uri: event.thumb || '' }}
                style={{
                  width: '100%',
                  height: 190,
                  resizeMode: 'cover',
                  marginBottom: 20,
                }}
                onLayout={(e) => {
                  var { height } = e.nativeEvent.layout;
                  setThumbHeight(height);
                }}
              />
            </View>
          )}
          <View style={[styles.container, { marginTop: 4 }]}>
            <View style={styles.headerWrapper}>
              <Text style={styles.headerText}>{event?.title}</Text>
            </View>
            <Text
              style={[
                styles.dateText,
                {
                  fontSize: 14,
                  marginTop: 10,
                },
              ]}
            >
              {convertUnixToUTCDate(event.start).format('DD-MM-YYYY')}
              <Text style={{ paddingHorizontal: 5 }}>{` - `}</Text>
              {convertUnixToUTCDate(event.end).format('DD-MM-YYYY')}
            </Text>
          </View>
          <View style={styles.container}>
            <View
              onLayout={(e) => {
                var { height } = e.nativeEvent.layout;
                setContentHeight(height);
              }}
            >
              {
                <RenderHtml
                  contentWidth={width - 40}
                  source={{ html: event.content }}
                  tagsStyles={{
                    body: {
                      color: COLORS.DARKER_GREY,
                      fontFamily: FONTS.AvenirMedium,
                    },
                    p: {
                      color: COLORS.DARKER_GREY,
                      fontFamily: FONTS.AvenirMedium,
                    },
                  }}
                />
              }
            </View>
          </View>
          {event.signup_available && (
            <View style={styles.container}>
              <View style={{ paddingVertical: 30, alignSelf: 'flex-start' }}>
                <Button
                  text="SIGN UP"
                  style={STYLE_VARS.blueButton}
                  onPress={() => {
                    if (scrollViewRef.current) {
                      scrollViewRef.current.scrollTo({
                        y: signUpScrollHeight,
                        animated: true,
                      });
                    }
                  }}
                />
              </View>
            </View>
          )}

          <View style={styles.container}>
            <View style={{ flexDirection: 'column' }}>
              {buttons.map((button) => (
                <TouchableOpacity
                  key={button.title}
                  style={{
                    marginBottom: 10,
                    alignItems: 'center',
                    flexDirection: 'row',
                    alignSelf: 'flex-start',
                    paddingVertical: 5,
                  }}
                  onPress={button.onPress}
                >
                  <Image
                    source={button.icon}
                    style={{
                      width: 18,
                      height: 18,
                      resizeMode: 'contain',
                      marginRight: 10,
                    }}
                  />
                  <Text
                    style={{
                      fontFamily: FONTS.IowanOldStyleBTRoman,
                      fontSize: 14,
                      fontWeight: '500',
                      color: STYLE_VARS.textColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    {button.title}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          {event?.poster && (
            <View style={styles.container}>
              <Image
                source={{ uri: event.poster }}
                style={{
                  width: '100%',
                  height: 530,
                  resizeMode: 'cover',
                  marginVertical: 20,
                }}
                onLayout={(e) => {
                  var { height } = e.nativeEvent.layout;
                  setPosterHeight(height);
                }}
              />
            </View>
          )}
          {event.signup_available && (
            <View style={styles.container}>
              <EventSignUpForm onSubmitHandler={onSubmitHandler} />
            </View>
          )}
        </View>
      ) : isLoading === true ? (
        <View style={{ marginBottom: 20 }}>
          <Loading />
        </View>
      ) : (
        <></>
      )}
      <Footer />
      {isSubmitting === true && <Loading isFullScreen={true} />}
    </ScrollView>
  );
};

export default EventDetail;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  headerWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerText: {
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 16,
    fontWeight: '500',
    color: STYLE_VARS.textColor,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  lineHeader: {
    width: 30,
    height: 1,
    backgroundColor: STYLE_VARS.primaryColor,
  },
  dateText: {
    textAlign: 'center',
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontSize: 18,
    color: COLORS.DARKER_GREY,
  },
});
